import React, { useState, useEffect } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { tasksAPI } from '../../services/api';

/**
 * Miniatura de una foto de tarea. El endpoint exige token, asi que una etiqueta
 * <img src="/api/..."> no serviria: hay que descargarla como blob.
 *
 * Cuando la foto ya se ha trasladado a Drive, el fichero local se borra y el
 * backend responde 410: se muestra "archivada" en lugar de un hueco roto.
 */
export default function TaskPhoto({ filename, height = 120, clickable = true, driveLink }) {
  const [url, setUrl] = useState(null);
  const [state, setState] = useState('loading');   // loading | ok | archived | missing

  useEffect(() => {
    if (!filename) return undefined;
    let objectUrl = null;
    let cancelled = false;

    tasksAPI.photoBlob(filename)
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
        setState('ok');
      })
      .catch((err) => {
        if (cancelled) return;
        setState(err.response?.status === 410 ? 'archived' : 'missing');
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [filename]);

  if (state === 'loading') {
    return <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 1 }} />;
  }

  if (state === 'archived') {
    const content = (
      <Box sx={{ height, display: 'flex', flexDirection: 'column', alignItems: 'center',
                 justifyContent: 'center', gap: 0.5, border: '1px dashed', borderColor: 'divider',
                 borderRadius: 1, color: 'text.secondary', p: 1 }}>
        <CloudDoneIcon fontSize="small" color="success" />
        <Typography variant="caption" align="center" sx={{ lineHeight: 1.2 }}>
          Archivada en Drive
        </Typography>
      </Box>
    );
    return driveLink
      ? <Box component="a" href={driveLink} target="_blank" rel="noopener noreferrer"
          sx={{ display: 'block', textDecoration: 'none' }}>{content}</Box>
      : content;
  }

  if (state === 'missing') return null;

  const img = (
    <Box component="img" src={url} alt="Foto de la tarea"
      sx={{ width: '100%', height, objectFit: 'cover', display: 'block',
            borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
  );

  if (!clickable) return img;

  return (
    <Box component="a" href={url} target="_blank" rel="noopener noreferrer"
      sx={{ display: 'block' }}>
      {img}
    </Box>
  );
}
